import type { TemplateParams } from './useTemplateQuery'
import type { Template } from '~/utils/solr/template'
import { SolrParamsKey } from '#shared/solr'
import { extractLabel } from '#shared/utils/solr-facet'
import { useTemplateQuery } from './useTemplateQuery'
export interface TemplateFilterState {
  searchKeyword: string
  showOnlyFavorites: boolean
  selectedLegalArea: { value: string, label: string }[]
  selectedAuthor: { value: string, label: string }[]
  sortFilter: { value: string, label: string }
}

export type TemplateWithFavorite = Template & { isFavorite: boolean }

export function templateMatches(template: TemplateWithFavorite, state: TemplateFilterState): boolean {
  const authors = template.authorName ?? []

  if (!matchesAllSearchTerms(state.searchKeyword, [template.title || '', ...authors])) {
    return false
  }

  if (state.showOnlyFavorites && !template.isFavorite) {
    return false
  }

  if (state.selectedLegalArea.length > 0) {
    const areas = new Set(template.legal_areas ?? [])
    if (!state.selectedLegalArea.some(area => areas.has(area.value))) {
      return false
    }
  }

  if (state.selectedAuthor.length > 0) {
    const names = new Set(authors)
    if (!state.selectedAuthor.some(author => names.has(author.value))) {
      return false
    }
  }

  return true
}

function buildLegalAreaOptions(docs: { legal_areas?: string[] | null }[]): { value: string, label: string }[] {
  const areas = new Set<string>()
  for (const doc of docs) {
    for (const area of doc.legal_areas ?? []) {
      areas.add(area)
    }
  }

  return Array.from(areas)
    .map(area => ({ value: area, label: extractLabel(area) }))
    .sort((a, b) => compareLocaleNorwegian(a.label, b.label))
}

function buildAuthorOptions(docs: { authorName?: string[] | null }[]): { value: string, label: string }[] {
  const authors = new Set<string>()
  for (const doc of docs) {
    for (const name of doc.authorName ?? []) {
      const trimmed = name.trim()
      if (trimmed) {
        authors.add(trimmed)
      }
    }
  }
  return Array.from(authors)
    .map(author => ({ value: author, label: author }))
    .sort((a, b) => compareLocaleNorwegian(getLastName(a.label), getLastName(b.label)))
}

export const TEMPLATES_FACET_CONFIG = {
  selectedLegalArea: {
    docKeys: template => template.legal_areas ?? [],
    optionKey: value => value,
  },
  selectedAuthor: {
    docKeys: template => template.authorName ?? [],
    optionKey: value => value,
  },
} as const satisfies Partial<Record<FacetKeys<TemplateFilterState>, FacetIndexConfig<TemplateWithFavorite>>>

export function _useTemplateFilters() {
  const searchKeyword = useState<string>('template-filter-search', () => '')
  const sortFilter = useState<{ value: string, label: string }>('template-filter-sort', () => TEMPLATES_SORTING_OPTIONS[0])
  const showOnlyFavorites = useState<boolean>('template-filter-favorites', () => false)
  const selectedLegalArea = useState<{ value: string, label: string }[]>('template-filter-legal-area', () => [])
  const selectedAuthor = useState<{ value: string, label: string }[]>('template-filter-author', () => [])

  const searchLegalArea = ref<string>('')
  const searchAuthor = ref<string>('')

  const { isFavorite } = useFavorites()

  const config = useRuntimeConfig()
  const isUsingProxy = !!config.solrProxyUrl && !!config.solrProxyUser && !!config.solrProxyPassword
  const isProdEnv = config.public?.env === 'production'
  const defaultNodeType = (isUsingProxy || isProdEnv) ? 'contract_template' : 'juridika_template'

  const solrParams = computed<TemplateParams>(() => ({
    [SolrParamsKey.Templates]: {
      nodeType: defaultNodeType,
      sort: 'title ASC',
    },
  }))

  const { data: templatesData, isLoading } = useTemplateQuery(solrParams)

  const baseTemplates = computed<TemplateWithFavorite[]>(() => {
    const templates = (templatesData.value?.response?.docs ?? []) as Template[]
    return templates.map(template => ({
      ...template,
      isFavorite: isFavorite('templates', template.id),
    }))
  })

  const legalAreaOptions = computed(() => buildLegalAreaOptions(baseTemplates.value))
  const authorOptions = computed(() => buildAuthorOptions(baseTemplates.value))

  const committedState = computed<TemplateFilterState>(() => ({
    searchKeyword: searchKeyword.value,
    showOnlyFavorites: showOnlyFavorites.value,
    selectedLegalArea: selectedLegalArea.value,
    selectedAuthor: selectedAuthor.value,
    sortFilter: sortFilter.value,
  }))

  const countForFacet = createCountWithFilters(baseTemplates, committedState, templateMatches)

  const facetCounts = useFacetIndex(baseTemplates, committedState, templateMatches, TEMPLATES_FACET_CONFIG)

  const { selected: legalAreaSelected, toggle: toggleLegalArea, facetOptions: legalAreaFacetOptions } = useFacetFilter(selectedLegalArea, legalAreaOptions, facetCounts.selectedLegalArea)

  const { selected: authorSelected, toggle: toggleAuthor, facetOptions: authorFacetOptions } = useFacetFilter(selectedAuthor, authorOptions, facetCounts.selectedAuthor)

  const filteredTemplates = computed<Template[]>(() => {
    return baseTemplates.value.filter(template => templateMatches(template, committedState.value))
  })

  const sortedFilteredTemplates = computed<Template[]>(() => {
    const list = [...filteredTemplates.value]
    if (sortFilter.value.value === 'date') {
      const getTimestamp = (template: Template) => {
        const raw = Array.isArray(template.lastUpdatedByEditor) ? template.lastUpdatedByEditor[0] : template.lastUpdatedByEditor
        return raw ? Date.parse(raw) || 0 : 0
      }
      return list.sort((a: Template, b: Template) => getTimestamp(b) - getTimestamp(a))
    }

    return list.sort((a: Template, b: Template) => {
      const titleA = normalizeNorwegianSort(a.title || '')
      const titleB = normalizeNorwegianSort(b.title || '')
      return compareLocaleNorwegian(titleA, titleB)
    })
  })

  const numFound = computed(() => filteredTemplates.value.length)

  function countWithFilters(overrides: Partial<TemplateFilterState> = {}): number {
    return countForFacet(overrides)
  }

  return {
    searchKeyword,
    sortFilter,
    showOnlyFavorites,
    selectedLegalArea,
    selectedAuthor,
    legalAreaOptions,
    authorOptions,
    legalAreaSelected,
    authorSelected,
    toggleLegalArea,
    toggleAuthor,
    legalAreaFacetOptions,
    authorFacetOptions,
    searchLegalArea,
    searchAuthor,
    templates: sortedFilteredTemplates,
    numFound,
    countWithFilters,
    isLoading,
  }
}

export const useTemplateFilters = createSharedComposable(_useTemplateFilters)
